import { useMemo } from 'react';

import isUndefinedOrNull from '@zenbusiness/application-commons-utils/isUndefinedOrNull';
import Divider from '@zenbusiness/zui/Divider';
import Grid from '@zenbusiness/zui/Grid';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import FieldDetail from '#client/components/TransactionDetailModal/components/FieldDetail';
import { formatAccountNumber } from '#client/utils/text';

export default function DebitCardDetail({
  cardCompany,
  cardHolderFirstName,
  cardHolderLastName,
  cardLastFourDigits,
  cardBillingAddress
}) {
  const shouldReturnNull = [
    cardCompany,
    cardHolderFirstName,
    cardHolderLastName,
    cardLastFourDigits,
    cardBillingAddress
  ].every((field) => isUndefinedOrNull(field));

  const formatCardNumber = formatAccountNumber({
    accountNumber: cardLastFourDigits ?? '',
    formatCharacter: '•',
    numberFormatCharacter: 2
  });
  const cardHolderName = [cardHolderFirstName, cardHolderLastName].join(' ');

  const { address1, address2, state, city, country } = cardBillingAddress ?? {};
  const cityText = city ?? '';
  const postalCodeText = country ?? '';
  const stateText = state ?? '';
  const streetText = address1 ?? '';
  const street2Text = address2 ?? '';

  const addressField = useMemo(() => {
    if (!cardBillingAddress) {
      return null;
    }

    return (
      <Grid
        item
        lg={4}
        xs={12}
      >
        <Stack
          direction="column"
          gap="8px"
        >
          <Text
            color="tertiary"
            size="xs"
            uppercase
          >
            LOCATION
          </Text>
          <>
            <Text
              color="primary"
              size="lg"
            >
              {streetText}
            </Text>
            {street2Text && (
              <Text
                color="primary"
                size="lg"
              >
                {street2Text}
              </Text>
            )}
            <Text
              color="primary"
              size="lg"
            >{`${cityText}, ${stateText} ${postalCodeText}`}</Text>
          </>
        </Stack>
      </Grid>
    );
  }, [cardBillingAddress, cityText, postalCodeText, stateText, street2Text, streetText]);

  if (shouldReturnNull) {
    return null;
  }

  return (
    <>
      <Divider />
      <Grid
        columnSpacing="8px"
        container
        pb={{ xs: '24px', md: '56px' }}
        pt={{ xs: '24px', md: '32px' }}
        px={{ xs: '24px', md: '32px' }}
        rowSpacing={{ xs: '16px', md: '32px' }}
      >
        <Grid
          item
          xs={12}
        >
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
          >
            <Text
              color="primary"
              size="xl"
              weight="medium"
            >
              Card details
            </Text>
          </Stack>
        </Grid>
        <FieldDetail
          label="CARD TYPE"
          value={cardCompany}
        />
        <FieldDetail
          label="CARD NUMBER"
          value={formatCardNumber}
        />
        <FieldDetail
          label="CARDHOLDER NAME"
          value={cardHolderName}
        />
        {addressField}
      </Grid>
    </>
  );
}

DebitCardDetail.propTypes = {
  cardCompany: PropTypes.string,
  cardLastFourDigits: PropTypes.string,
  cardHolderFirstName: PropTypes.string,
  cardHolderLastName: PropTypes.string,
  cardBillingAddress: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    state: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string
  })
};
